/**
 * AdminSetupPage: the authenticated admin surface that wraps
 * `CreateInterviewForm` and persists the resulting form to the database.
 *
 * Extracted from the former monolithic `App.tsx` (audit A1 / item 28). Lives
 * behind `ProtectedRoute` at `/admin/setup`; the respondent flow
 * (`InterviewFlow`) is a separate component and never writes to the DB.
 *
 * @module components/AdminSetupPage
 */

import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSupabase } from '../lib/SupabaseProvider';
import { useOrg } from '../lib/OrgProvider';
import { InterviewContext } from '../types';
import CreateInterviewForm, { type SetupOptions } from './CreateInterviewForm';
import { clearSetupDraft } from './setupDraft';
import { AuthoringHeader } from './authoring/AuthoringHeader';
import {
  createInterviewSession,
  updateSessionSettings,
  getInterviewSessionCount,
} from '../services/interviewService';
import { captureException, captureEvent } from '../lib/telemetry/posthog/frontend';
import { buildConversationCreationProps } from '../lib/telemetry/conversation-creation-contract';
import { buildOnboardingProps } from '../lib/telemetry/onboarding-contract';

/** Admin "Create" page: builds a form_schema and saves it as a session. */
export const AdminSetupPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSupabase();
  const { activeOrg } = useOrg();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handleSetupComplete = async (
    context: InterviewContext,
    options?: SetupOptions,
  ) => {
    if (!user?.id) {
      setError('You must be logged in to create one.');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Form-first redesign (Phase 1): the wizard authors a `form_schema`
      // and does NOT author `details` directly. The service auto-derives a
      // legacy `details` blob from `form_schema` so the unmodified
      // `live-session` worker keeps reading a useful prompt source. Passing
      // `details` here would override that derivation; we deliberately omit
      // it so the bridge stays in charge.
      const sessionInput = {
        target_name: context.targetName,
        target_url: context.targetUrl,
        goal: context.goal,
        product_research: context.productResearch,
        output_language: context.outputLanguage,
        brand_id: context.brandId,
        organization_id: activeOrg?.id,
        form_schema: options?.formSchema,
        ...(options?.conversationConfig !== undefined ? { conversation_config: options.conversationConfig } : {}),
      };
      const { session } = await createInterviewSession(sessionInput, user.id, {
        videoEnabled: options?.videoEnabled,
      });

      // Apply campaign settings if provided
      if (options?.campaignSettings) {
        await updateSessionSettings(session.id, options.campaignSettings);
      }

      // The build is now persisted to the DB, so drop the in-session draft
      // safety net (otherwise the next visit to /admin/setup would resurrect
      // the just-created form).
      clearSetupDraft();

      captureEvent('conversation_creation.saved', buildConversationCreationProps(context.brandId, {
        sessionId: session.id,
        videoEnabled: options?.videoEnabled,
        probingDepth: options?.campaignSettings?.probing_depth,
        fieldCount: options?.formSchema?.length,
      }));

      // Check if this is the user's first campaign for onboarding telemetry.
      // We check the count in the current org.
      if (activeOrg?.id) {
        const campaignCount = await getInterviewSessionCount(activeOrg.id);
        if (campaignCount === 1) {
          captureEvent('onboarding.first_conversation_created', buildOnboardingProps());
        }
      }

      // Navigate to campaign page and auto-open InviteModal
      void navigate(`/admin/interviews/${session.id}`, { state: { showInvite: true } });
    } catch (err) {
      captureException(err instanceof Error ? err : String(err), 'AdminSetupPage.handleSetupComplete', { targetName: context.targetName, userId: user?.id });
      setError(err instanceof Error ? err.message : 'Failed to create session');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pt-16 md:pt-0">
      <AuthoringHeader title="Create" breadcrumbs={[{ label: 'Home', href: '/admin' }, { label: 'Create' }]} />

      {/* Error Message */}
      {error && (
        <div className="container mx-auto px-4 py-4">
          <div className="p-4 bg-wash-danger rounded-lg text-foreground">
            {error}
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {saving && (
        <div className="fixed inset-0 bg-background/80 flex items-center justify-center z-50">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-volt/30 border-t-volt rounded-full animate-spin mx-auto mb-4" />
            <p className="text-foreground">Creating...</p>
          </div>
        </div>
      )}

      {/* Welcome Screen Form */}
      {/* `key` is bumped by the sidebar's "Create" button (via
          `location.state.resetKey`) so clicking it while already on
          /admin/setup forces a full remount and clears in-progress wizard
          state. Default key keeps the form stable across unrelated re-renders. */}
      <CreateInterviewForm
        key={location.state?.resetKey ?? 'fresh'}
        onSetupComplete={handleSetupComplete}
        initialContext={location.state?.initialContext}
        initialFields={location.state?.initialFields}
        initialConversationConfig={location.state?.initialConversationConfig}
      />
    </div>
  );
};

export default AdminSetupPage;
